import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

export default function ShowcaseGallery({ images = [] }) {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const next = useCallback((e) => {
    if (e) e.stopPropagation();
    setActive((i) => (i === null ? null : (i + 1) % images.length));
  }, [images.length]);
  const prev = useCallback((e) => {
    if (e) e.stopPropagation();
    setActive((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);

  useEffect(() => {
    if (active === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [active, close, next, prev]);

  if (!images || images.length === 0) return null;

  return (
    <section className="py-20 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <p className="text-primary text-xs font-bold tracking-widest mb-3">GALLERY</p>
        <h2 className="text-white font-black text-3xl md:text-4xl uppercase mb-10">Every Angle Covered</h2>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`group relative overflow-hidden bg-[#0d0d0f] border border-white/5 hover:border-primary/40 transition-colors ${
                i === 0 ? 'col-span-2 row-span-2 aspect-square' : 'aspect-square'
              }`}
            >
              <img
                src={img}
                alt={`Gallery ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          >
            <button onClick={close} className="absolute top-5 right-5 text-white/60 hover:text-white p-2 z-10">
              <X className="w-6 h-6" />
            </button>
            <button onClick={prev} className="absolute left-4 md:left-8 text-white/60 hover:text-white p-2 z-10">
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button onClick={next} className="absolute right-4 md:right-8 text-white/60 hover:text-white p-2 z-10">
              <ChevronRight className="w-8 h-8" />
            </button>
            <motion.img
              key={active}
              src={images[active]}
              alt=""
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-full max-h-[88vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-xs tracking-widest font-bold">
              {active + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}